import { useState, useEffect } from 'react';
import {
  List,
  ListSubheader,
  ListItemSecondaryAction,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Divider,
  Typography,
  Box,
  Tooltip,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  AlertDialog,
  AvatarsCell,
  selectUser,
  sortString,
} from '@centerpiece/components';
import {
  PersonAddOutlined,
  Edit,
  Delete,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useDeleteSubscriberMutation, useGetSubscribersQuery } from 'store/api';
import { ContactTooltip } from './ContactTooltip';
import { toast } from 'react-toastify';
import { AccountComponent } from './AccountComponent';
import { useSelector } from 'react-redux';
import { useTutorialContext } from 'tutorials/tutorial.context';

export const AccountList = () => {
  const user = useSelector(selectUser);
  const [openAlert, setOpenAlert] = useState<number>();
  const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [hover, setHover] = useState<boolean>(false);
  const handleOpenSpeedDial = () => setOpenSpeedDial(true);
  const handleCloseSpeedDial = () => setOpenSpeedDial(false);
  const [openDialog, setOpenDialog] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
  }>({ open: false });
  const { data: subscribers = [] } = useGetSubscribersQuery({});
  const [deleteSubscriber] = useDeleteSubscriberMutation();

  const { state, setState } = useTutorialContext();

  useEffect(() => {
    if (state.tourActive) {
      setTimeout(function () {
        setState(currState => ({
          ...currState,
          run: true,
        }));
      }, 800);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <AlertDialog
        open={!!openAlert}
        title={`Delete account`}
        content={`Are you sure you want to delete this account?`}
        onClose={() => {
          setOpenAlert(undefined);
        }}
        onConfirm={async () => {
          if (openAlert && openAlert > 0) {
            try {
              await deleteSubscriber(openAlert);
              toast.success('Account successfully deleted');
            } catch (error) {
              toast.success('Unable to delete account');
            }
          }
          setOpenAlert(undefined);
        }}
      />
      {openDialog.open ? (
        <AccountComponent
          close={() => setOpenDialog({ open: false })}
          {...openDialog}
        />
      ) : (
        <>
          <List
            sx={{ width: '100%', bgcolor: 'background.paper' }}
            subheader={
              <ListSubheader disableSticky component="div" sx={{ p: 0 }}>
                <Box sx={{ pt: 0, pb: { xs: 3, sm: 1 } }}>
                  <Grid container alignItems={'bottom'} spacing={0}>
                    <Grid item xs={12} sm={5} md={3}>
                      <Typography
                        variant="h6"
                        component={'div'}
                        sx={{ color: 'black' }}
                      >
                        Account List
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={7}
                      md={9}
                      sx={{ textAlign: 'right' }}
                    >
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Search Contacts"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        onChange={event =>
                          setSearch((event?.target as any)?.value)
                        }
                        sx={{ maxWidth: { xs: 'none', sm: 340 } }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </ListSubheader>
            }
          >
            {[...(subscribers || [])]
              ?.filter(sub => sub?.id !== user?.subscriber)
              ?.filter(
                sub =>
                  sub.contact_r?.firstname
                    ?.toLowerCase()
                    ?.includes(search.toLowerCase()) ||
                  sub.contact_r?.lastname
                    ?.toLowerCase()
                    ?.includes(search.toLowerCase()) ||
                  sub.contact_r?.email
                    ?.toLowerCase()
                    ?.includes(search.toLowerCase())
              )
              ?.sort((a, b) =>
                sortString(
                  `${a.contact_r?.firstname} ${a.contact_r?.lastname}`,
                  `${b.contact_r?.firstname} ${b.contact_r?.lastname}`
                )
              )
              ?.map((sub, index) => (
                <>
                  <Divider />
                  <ListItem
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    onClick={() =>
                      setOpenDialog({ open: true, id: sub.id, disabled: true })
                    }
                    key={index}
                    sx={hover ? { backgroundColor: 'grey' } : {}}
                  >
                    <ListItemAvatar>
                      <AvatarsCell
                        data={[{ ...sub, role_r: null } as any]}
                        tooltip={ContactTooltip}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <span>
                          <b>
                            {' '}
                            {!!sub?.contact_r?.firstname
                              ? `${sub?.contact_r.firstname} ${sub?.contact_r.lastname}`
                              : sub?.contact_r.email}
                          </b>{' '}
                        </span>
                      }
                      // secondary={!!sub?.contact_r?.email ? `  (${sub?.contact_r.email})` : ``}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={'Edit'}>
                        <IconButton
                          onClick={() =>
                            setOpenDialog({ open: true, id: sub?.id })
                          }
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={'Delete'}>
                        {openAlert && openAlert === sub.id ? (
                          <IconButton>
                            <CircularProgress size={22} />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => setOpenAlert(sub.id)}>
                            <Delete />
                          </IconButton>
                        )}
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </>
              ))}
          </List>
          <SpeedDial
            ariaLabel="SpeedDial basic example"
            sx={{
              position: 'fixed',
              right: 0,
              px: 4,
              width: '100%',
              boxSizing: 'border-box',
              bottom: 64,
              alignItems: 'flex-end',
            }}
            icon={<SpeedDialIcon id="speeddial" />}
            onClose={handleCloseSpeedDial}
            onOpen={handleOpenSpeedDial}
            open={openSpeedDial}
          >
            <SpeedDialAction
              key={'account'}
              icon={<PersonAddOutlined id={'account-speeddial'} />}
              tooltipTitle={'Add Account'}
              tooltipOpen
              onClick={() => setOpenDialog({ open: true })}
            />
          </SpeedDial>
        </>
      )}
    </>
  );
};
